/**
 * useAuth – lightweight auth state hook.
 *
 * Reads the token + user object written to localStorage by Auth.jsx on login:
 *   localStorage.setItem("authToken", data.token)
 *   localStorage.setItem("user", JSON.stringify(data.user))
 *
 * Calling logout() clears both keys and invokes the onLogout callback,
 * which in App.jsx calls handleLogout() → setIsLoggedIn(false).
 *
 * Persistence across refreshes is handled by App.jsx initialising
 * isLoggedIn from localStorage (not useState(false)).
 */

import { useState, useCallback } from "react";

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const readToken = () => localStorage.getItem("authToken") || null;

/**
 * @param {Function} [onLogout] – called after localStorage is cleared
 * @returns {{ user, token, isAuthenticated, logout }}
 */
export function useAuth(onLogout) {
  const [user, setUser] = useState(readUser);
  const [token, setToken] = useState(readToken);

  const logout = useCallback(() => {
    // Clear persisted session
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");

    // Reset local hook state
    setUser(null);
    setToken(null);

    // Notify App to flip isLoggedIn → false (returns to login screen)
    if (typeof onLogout === "function") {
      onLogout();
    }
  }, [onLogout]);

  return {
    user,
    token,
    isAuthenticated: Boolean(token),
    logout,
  };
}
